import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@repo/ui";
import { configurationApi } from "~/api";
import { useBrandCreationContext } from "~/pages/administration/brand-setting-page/contexts/brand-creation-context-provider";
import type { Brand } from "~/types/api";
import { fileToBase64 } from "~/utils/helpers";

type BrandsMutationContext = {
  previousBrands: Brand[] | undefined;
};

export const useCreateBrand = () => {
  const { state } = useBrandCreationContext();
  const { brandName, selectedCustomerId, imageFile, brandPaths } = state;
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const brandsQueryOptions = configurationApi.queryOptions("get", "/api/Brand");
  const brandsQueryKey = brandsQueryOptions.queryKey;
  const {
    mutateAsync: mutateBrand,
    isPending,
    isError,
    isSuccess,
  } = configurationApi.useMutation("post", "/api/Brand", {
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: brandsQueryKey });
      const { paths, ...restNewBrandData } = variables.body;

      const previousBrands = queryClient.getQueryData(brandsQueryKey);

      const optimisticBrand = {
        id: `temp-${Date.now()}`,
        ...restNewBrandData,
        brandPaths: paths,
      };
      // Optimistically update the cache
      queryClient.setQueryData(brandsQueryKey, (oldData: Brand[]) => {
        if (Array.isArray(oldData)) {
          return [...oldData, optimisticBrand];
        }
        return oldData;
      });
      return { previousBrands };
    },

    // onError, roll back
    onError: (_err, _variables, context) => {
      const previousBrands = (context as BrandsMutationContext)?.previousBrands;
      showToast("error", "Failed to create brand. Please try again.");
      if (previousBrands) {
        queryClient.setQueryData(brandsQueryKey, previousBrands);
      }
    },

    onSuccess: (data) => {
      // Replace the optimistic entry with the real one from the server
      queryClient.setQueryData(brandsQueryKey, (oldData: Brand[]) => {
        if (Array.isArray(oldData)) {
          return oldData.map((b) => (String(b.id).startsWith("temp-") ? data : b));
        }
        return oldData;
      });
      showToast("success", `Brand "${data.name}" created successfully.`);
    },

    // After success OR error, invalidate query.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: brandsQueryKey });
    },
  });

  const createBrand = async () => {
    const logo = imageFile ? await fileToBase64(imageFile) : "";
    await mutateBrand({
      body: {
        customerId: Number(selectedCustomerId),
        name: brandName,
        logo: logo ?? undefined,
        paths: brandPaths,
      },
    });
  };

  return { createBrand, isPending, isError, isSuccess };
};
